import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
  Tabs,
  Segmented,
  message,
} from 'antd';
import {
  ReloadOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  BellOutlined,
  RightOutlined,
  UnorderedListOutlined,
  FlagOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import {
  getMyDrafts,
  getWorkboard,
  getWorkboardActivity,
  removeWorkboardFollowup,
  upsertWorkboardFollowup,
} from '@/services/workboard.service';
import { responsibilityIntelligenceService } from '@/services/responsibilityIntelligence.service';
import { approvalService } from '@/services/approval.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ORDAN_TEAL } from '@/constants/brand';
import type {
  DueWindow,
  EnterpriseWorkItemProjection,
  OwnershipRole,
  WorkItemBucket,
  WorkboardActivityGroup,
} from '@/types/enterprise-work-item';

const { Title, Text, Paragraph } = Typography;

type TabKey = WorkItemBucket;
type KpiKey = 'action_required' | 'responsibilities' | 'waiting_on_others' | 'exceptions';
type DensityMode = 'compact' | 'comfortable';
type SortMode = 'impact' | 'waiting' | 'due' | 'document';

const DENSITY_KEY = 'ordan.workboard.density';
const GROUP_KEY = 'ordan.workboard.groupByType';

const OWNERSHIP_LABEL: Record<OwnershipRole, string> = {
  assignee: 'Assigned to you',
  approver: 'You approve',
  submitter: 'You submitted',
  draft_owner: 'Your draft',
  monitor: 'You monitor',
  department_owner: 'Department owner',
};

const TAB_HELP: Record<string, string> = {
  needs_action:
    'Executable work: approvals and tasks you must act on. Monitoring obligations live under Responsibilities.',
  responsibilities:
    'Business commitments you monitor (e.g. PO delivery). Not the same as an executable task — escalate when overdue.',
  initiated:
    'Documents you submitted. A returned item may also appear under Action. Counts are not additive across tabs.',
  waiting_on_others:
    'Submitted work waiting on someone else. The same document may also appear under Initiated.',
  overdue_at_risk:
    'True exceptions: overdue PO deliveries, SLA breach/critical, blocked, or approval escalation — not every aged item.',
  upcoming: 'Due today or within the next 7 days, and not yet overdue.',
};

const KPI_HELP: Record<KpiKey, string> = {
  action_required: 'Executable actions assigned to you (approvals + Unified Tasks).',
  responsibilities: 'Monitoring obligations (PO delivery approaching / due today) — not execute-now tasks.',
  waiting_on_others: 'Approvals you submitted that are still pending others.',
  exceptions:
    'Overdue deliveries, SLA / blocked / approval escalation. Open this KPI for a source breakdown.',
};

function ownershipLabel(role?: OwnershipRole): string | undefined {
  return role ? OWNERSHIP_LABEL[role] : undefined;
}

function readPref<T extends string>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return (v as T) || fallback;
  } catch {
    return fallback;
  }
}

function writePref(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function matchesDueWindow(row: EnterpriseWorkItemProjection, window: DueWindow): boolean {
  if (window === 'all') return true;
  if (window === 'critical') {
    return (
      row.exceptionKind === 'sla' ||
      row.exceptionKind === 'approval_overdue' ||
      row.priority === 'Critical' ||
      row.priority === 'High' ||
      row.slaRisk === 'critical' ||
      row.slaRisk === 'breach'
    );
  }
  const due = row.dueDate ? dayjs(row.dueDate.slice(0, 10)) : null;
  const today = dayjs().startOf('day');
  if ((row.overdueDays ?? 0) > 0 || (due && due.isBefore(today, 'day'))) {
    return window === 'overdue';
  }
  if (!due || !due.isValid()) return window === 'no_due';
  if (due.isSame(today, 'day')) return window === 'today';
  if (due.isBefore(today.add(7, 'day'), 'day')) return window === 'this_week';
  return window === 'later';
}

function impactScore(item: EnterpriseWorkItemProjection): number {
  let score = 0;
  if (item.exceptionKind === 'blocked') score += 400;
  if (item.exceptionKind === 'sla' || item.exceptionKind === 'approval_overdue') score += 350;
  if (item.priority === 'Critical') score += 200;
  else if (item.priority === 'High') score += 120;
  else if (item.priority === 'Medium') score += 40;
  if ((item.overdueDays ?? 0) > 0) score += Math.min(150, 50 + (item.overdueDays ?? 0));
  score += Math.min(80, item.waitingSinceDays ?? 0);
  return score;
}

function sortRows(rows: EnterpriseWorkItemProjection[], mode: SortMode): EnterpriseWorkItemProjection[] {
  const copy = [...rows];
  switch (mode) {
    case 'waiting':
      return copy.sort((a, b) => (b.waitingSinceDays ?? 0) - (a.waitingSinceDays ?? 0));
    case 'due':
      return copy.sort((a, b) => {
        const ad = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.POSITIVE_INFINITY;
        const bd = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.POSITIVE_INFINITY;
        return ad - bd;
      });
    case 'document':
      return copy.sort((a, b) =>
        String(a.documentNumber || a.title).localeCompare(String(b.documentNumber || b.title))
      );
    case 'impact':
    default:
      return copy.sort((a, b) => impactScore(b) - impactScore(a));
  }
}

function matchesSearch(row: EnterpriseWorkItemProjection, q: string): boolean {
  if (!q) return true;
  const hay = [
    row.documentNumber,
    row.title,
    row.businessDescription,
    row.actionRequired,
    row.partyName,
    row.contextSummary,
    row.module,
    row.entityType,
    row.status,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

function attentionSentence(kpis: {
  action_required?: number;
  responsibilities?: number;
  waiting_on_others?: number;
  exceptions?: number;
  upcoming?: number;
}): string {
  const action = kpis.action_required ?? 0;
  const monitors = kpis.responsibilities ?? 0;
  const waiting = kpis.waiting_on_others ?? 0;
  const exceptions = kpis.exceptions ?? 0;
  if (exceptions > 0) {
    return `${exceptions} exception${exceptions === 1 ? '' : 's'} · ${action} action · ${monitors} to monitor`;
  }
  if (action > 0 || monitors > 0) {
    return `${action} action required · ${monitors} responsibilit${monitors === 1 ? 'y' : 'ies'} · ${waiting} waiting`;
  }
  return 'Nothing urgent — your workboard is clear';
}

function humanGroupLabel(entityType: string): string {
  return String(entityType || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function QueueRow({
  row,
  density,
  onOpen,
  onCta,
  remind,
  remindLoading,
}: {
  row: EnterpriseWorkItemProjection;
  density: DensityMode;
  onOpen: () => void;
  onCta: () => void;
  remind?: () => void;
  remindLoading?: boolean;
}) {
  const isMonitor = row.source === 'responsibility_intelligence';
  /** Action + monitor rows: document-first title and More / open document. */
  const detailFirst = isMonitor || row.bucket === 'needs_action' || row.bucket === 'responsibilities';
  const title = detailFirst
    ? [row.documentNumber || row.title, row.partyName].filter(Boolean).join(' · ') ||
      row.businessDescription ||
      row.title
    : row.businessDescription || row.documentNumber || row.title;
  const isException = row.bucket === 'overdue_at_risk' || !!row.exceptionKind;
  const subtitle = isMonitor
    ? [row.partyName && !title.includes(row.partyName) ? row.partyName : null, row.contextSummary]
        .filter(Boolean)
        .join(' · ')
    : isException
      ? [
          row.businessDescription && row.businessDescription !== title ? row.businessDescription : null,
          (row.waitingSinceDays ?? 0) > 0 ? `${row.waitingSinceDays} days in process` : null,
          row.contextSummary,
        ]
          .filter(Boolean)
          .join(' · ')
      : [
          row.actionRequired || row.workflowStepLabel || row.status,
          row.businessDescription && row.businessDescription !== title ? row.businessDescription : null,
          row.contextSummary,
          (row.waitingSinceDays ?? 0) > 0 ? `${row.waitingSinceDays}d waiting` : null,
        ]
          .filter(Boolean)
          .join(' · ');
  const compact = density === 'compact';
  const openLabel = isMonitor ? 'View PO' : row.ctaLabel || 'Open document';
  const statusPill = isMonitor || row.status === 'Escalated' ? row.status : null;
  const monitorStatusColor =
    row.status?.startsWith('Overdue') || row.status === 'Overdue' || row.status === 'Escalated'
      ? 'red'
      : row.status === 'Due Today'
        ? 'volcano'
        : row.status?.startsWith('Due in') || row.status === 'Approaching'
          ? 'orange'
          : 'default';
  const showRemind =
    (row.bucket === 'waiting_on_others' || row.bucket === 'initiated') &&
    row.source === 'approval_inbox' &&
    !!row.sourceRefId &&
    row.status === 'pending_approval' &&
    !!remind;

  return (
    <div
      onClick={onOpen}
      style={{
        borderBottom: '1px solid #E8ECEB',
        padding: compact ? '10px 4px' : '12px 4px',
        minHeight: compact ? 52 : 72,
        background: '#fff',
        cursor: 'pointer',
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#F7F8F7';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#fff';
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
            flexWrap: compact ? 'nowrap' : 'wrap',
          }}
        >
          <Text
            strong
            ellipsis
            style={{ color: ORDAN_TEAL, maxWidth: compact ? '42%' : '100%' }}
          >
            {title}
          </Text>
          {row.exceptionKind && (
            <Tag color="red" style={{ margin: 0, flexShrink: 0 }}>
              {row.exceptionKind === 'sla'
                ? 'SLA'
                : row.exceptionKind === 'blocked'
                  ? 'Blocked'
                  : 'Escalation'}
            </Tag>
          )}
          {row.stale && (
            <Tooltip title={row.staleReason || 'No meaningful activity within the inactivity threshold.'}>
              <Tag color="default" style={{ margin: 0, flexShrink: 0 }}>
                Stale
              </Tag>
            </Tooltip>
          )}
          {row.personalFollowUpDate && (
            <Tooltip title={row.personalFollowUpNote || `Personal follow-up ${row.personalFollowUpDate}`}>
              <Tag color="blue" style={{ margin: 0, flexShrink: 0 }} icon={<FlagOutlined />}>
                {dayjs(row.personalFollowUpDate).format('DD MMM')}
              </Tag>
            </Tooltip>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <Text
            type="secondary"
            ellipsis
            style={{ fontSize: 13, display: 'block', maxWidth: '100%' }}
          >
            {subtitle}
          </Text>
        </div>
        {!compact && !detailFirst && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }} ellipsis>
            {[row.contextSummary, ownershipLabel(row.ownershipRole), row.currentResponsibleParty ? `With: ${row.currentResponsibleParty}` : null]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        )}
      </div>
      <Space size={4} style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
        {statusPill && (
          <Tag color={monitorStatusColor} style={{ margin: 0 }}>
            {statusPill}
          </Tag>
        )}
        {showRemind && (
          <Button size="small" loading={remindLoading} onClick={remind}>
            Remind
          </Button>
        )}
        {detailFirst ? (
          <>
            <Button size="small" onClick={onOpen}>
              More
            </Button>
            <Button
              size="small"
              type="primary"
              style={{ background: ORDAN_TEAL, borderColor: ORDAN_TEAL }}
              onClick={onCta}
            >
              {openLabel}
            </Button>
          </>
        ) : compact ? (
          <Button
            type="text"
            size="small"
            icon={<RightOutlined />}
            aria-label={row.ctaLabel || 'Open'}
            onClick={onCta}
          />
        ) : (
          <Button
            type="primary"
            size="small"
            style={{ background: ORDAN_TEAL, borderColor: ORDAN_TEAL }}
            onClick={onCta}
          >
            {row.ctaLabel || 'Open'}
          </Button>
        )}
      </Space>
    </div>
  );
}

function AttentionPanel({
  groups,
  overdueCount,
  personalFollowups,
  onNavigate,
}: {
  groups: WorkboardActivityGroup[];
  overdueCount: number;
  personalFollowups: Array<{
    title: string;
    followUpDate: string;
    overdue: boolean;
    actionRoute: string;
    note?: string;
  }>;
  onNavigate: (route?: string) => void;
}) {
  return (
    <div style={{ minWidth: 0, width: '100%' }}>
      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>
          Overdue actions
        </Text>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {overdueCount > 0
            ? `${overdueCount} pending action${overdueCount === 1 ? '' : 's'} past due date (Due filter). Not the same as Exceptions KPI.`
            : 'No due-date overdue items in your action queue.'}
        </Text>
      </div>
      {personalFollowups.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Personal follow-ups
          </Text>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {personalFollowups.slice(0, 6).map((f) => (
              <div
                key={`${f.actionRoute}:${f.followUpDate}`}
                style={{ display: 'flex', gap: 8, minWidth: 0, cursor: 'pointer' }}
                onClick={() => onNavigate(f.actionRoute)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong ellipsis style={{ display: 'block' }}>
                    {f.title}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {f.overdue ? 'Overdue · ' : ''}
                    {dayjs(f.followUpDate).format('DD MMM YYYY')}
                    {f.note ? ` · ${f.note}` : ''}
                  </Text>
                </div>
                <FlagOutlined style={{ color: f.overdue ? '#cf1322' : ORDAN_TEAL, flexShrink: 0 }} />
              </div>
            ))}
          </Space>
        </div>
      )}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Recent updates
      </Text>
      {!groups.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recent updates" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
          {groups.slice(0, 12).map((g) => {
            const heading = g.documentNumber || g.title;
            const summary = g.summary || g.items[0]?.message || '';
            return (
              <div
                key={g.key}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-start',
                  minWidth: 0,
                  width: '100%',
                  cursor: g.actionRoute ? 'pointer' : 'default',
                }}
                onClick={() => g.actionRoute && onNavigate(g.actionRoute)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 8,
                      minWidth: 0,
                      width: '100%',
                    }}
                  >
                    <Text strong ellipsis style={{ flex: 1, minWidth: 0 }}>
                      {heading}
                    </Text>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {dayjs(g.latestAt).format('DD MMM HH:mm')}
                    </Text>
                  </div>
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 12,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: '16px',
                      maxHeight: 32,
                    }}
                  >
                    {summary}
                    {g.eventCount > 1 ? ` · ${g.eventCount} events` : ''}
                  </Text>
                </div>
                {g.actionRoute && (
                  <RightOutlined style={{ color: '#66727A', flexShrink: 0, marginTop: 4 }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

type QueueGroup = {
  key: string;
  label: string;
  rows: EnterpriseWorkItemProjection[];
};

function buildGroups(
  rows: EnterpriseWorkItemProjection[],
  enabled: boolean
): QueueGroup[] {
  if (!enabled) return [{ key: 'all', label: 'All', rows }];
  const map = new Map<string, EnterpriseWorkItemProjection[]>();
  for (const row of rows) {
    const key = row.entityType || 'other';
    const list = map.get(key) || [];
    list.push(row);
    map.set(key, list);
  }
  const groups: QueueGroup[] = [];
  for (const [key, list] of map.entries()) {
    groups.push({ key, label: humanGroupLabel(key), rows: list });
  }
  groups.sort((a, b) => b.rows.length - a.rows.length);
  return groups;
}

export const MyWorkboardPage: React.FC = () => {
  const { t } = useTranslation('workboard');
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('needs_action');
  const [kpiFilter, setKpiFilter] = useState<KpiKey | null>(null);
  const [dueWindow, setDueWindow] = useState<DueWindow>('all');
  const [selected, setSelected] = useState<EnterpriseWorkItemProjection | null>(null);
  const [density, setDensity] = useState<DensityMode>(() => readPref(DENSITY_KEY, 'compact'));
  const [groupByType, setGroupByType] = useState(() => {
    try {
      return localStorage.getItem(GROUP_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('impact');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [followUpDate, setFollowUpDate] = useState<string | null>(null);
  const [followUpNote, setFollowUpNote] = useState('');
  const queryClient = useQueryClient();
  const userId = user?.id;
  const companyId = user?.company_id;

  const displayName = useMemo(() => {
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    if (name) return name;
    return user?.username || user?.email || 'User';
  }, [user]);

  const query = useQuery({
    queryKey: ['home-workboard', userId, companyId],
    queryFn: () => getWorkboard({ sync: false }),
    enabled: Boolean(userId),
    staleTime: 60_000,
  });

  const activityQuery = useQuery({
    queryKey: ['home-workboard-activity', userId, companyId],
    queryFn: () => getWorkboardActivity(30),
    enabled: Boolean(userId),
    staleTime: 60_000,
  });

  const draftsQuery = useQuery({
    queryKey: ['home-drafts', userId, companyId],
    queryFn: () => getMyDrafts(),
    enabled: Boolean(userId),
    staleTime: 120_000,
  });

  React.useEffect(() => {
    if (!userId || !companyId || !query.isSuccess) return;
    const sessionSyncKey = `workboard-synced:${userId}:${companyId}`;
    try {
      if (sessionStorage.getItem(sessionSyncKey) === '1') return;
      sessionStorage.setItem(sessionSyncKey, '1');
    } catch {
      /* private mode */
    }
    let cancelled = false;
    void getWorkboard({ sync: true })
      .then((fresh) => {
        if (!cancelled) queryClient.setQueryData(['home-workboard', userId, companyId], fresh);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [userId, companyId, query.isSuccess, queryClient]);

  const remindMutation = useMutation({
    mutationFn: (instanceId: string) => approvalService.remindInstance(instanceId),
    onSuccess: (res) => {
      if (res.deduped) message.info('Reminder already sent today');
      else message.success(`Reminder sent (${res.sent})`);
      void activityQuery.refetch();
    },
    onError: (e: Error) => message.error(e.message || 'Remind failed'),
  });

  const followUpMutation = useMutation({
    mutationFn: async () => {
      if (!selected || !followUpDate) throw new Error('Pick a follow-up date');
      return upsertWorkboardFollowup({
        entityType: selected.entityType,
        entityId: selected.entityId,
        documentNumber: selected.documentNumber,
        followUpDate,
        note: followUpNote || undefined,
        title: selected.businessDescription || selected.title,
        actionRoute: selected.actionRoute,
      });
    },
    onSuccess: () => {
      message.success('Personal follow-up saved');
      void query.refetch();
    },
    onError: (e: Error) => message.error(e.message || 'Could not save follow-up'),
  });

  const clearFollowUpMutation = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('No item selected');
      return removeWorkboardFollowup(selected.entityType, selected.entityId);
    },
    onSuccess: () => {
      message.success('Follow-up cleared');
      setFollowUpDate(null);
      setFollowUpNote('');
      void query.refetch();
    },
    onError: (e: Error) => message.error(e.message || 'Could not clear follow-up'),
  });

  const acknowledgeRieMutation = useMutation({
    mutationFn: async () => {
      if (!selected?.sourceRefId) throw new Error('No obligation selected');
      return responsibilityIntelligenceService.acknowledge(selected.sourceRefId);
    },
    onSuccess: () => {
      message.success('Delivery monitor acknowledged');
      void query.refetch();
    },
    onError: (e: Error) => message.error(e.message || 'Could not acknowledge'),
  });

  React.useEffect(() => {
    if (!selected) {
      setFollowUpDate(null);
      setFollowUpNote('');
      return;
    }
    setFollowUpDate(selected.personalFollowUpDate || dayjs().add(3, 'day').format('YYYY-MM-DD'));
    setFollowUpNote(selected.personalFollowUpNote || '');
  }, [selected]);

  const data = query.data;

  const kpis = useMemo(() => {
    const k = data?.kpis;
    return {
      action_required: k?.action_required ?? k?.needs_action ?? 0,
      responsibilities: k?.responsibilities ?? data?.responsibilities?.length ?? 0,
      waiting_on_others: k?.waiting_on_others ?? 0,
      upcoming:
        k?.upcoming ?? (data?.upcoming?.today.length ?? 0) + (data?.upcoming?.this_week.length ?? 0),
      exceptions: k?.exceptions ?? k?.overdue_at_risk ?? 0,
      unread_notifications: k?.unread_notifications ?? 0,
    };
  }, [data]);

  const activityGroups = useMemo(() => {
    if (activityQuery.data?.groups?.length) return activityQuery.data.groups;
    const items = activityQuery.data?.items ?? [];
    const map = new Map<string, typeof items>();
    for (const item of items) {
      const key =
        item.entityType && item.entityId
          ? `${item.entityType}::${item.entityId}`
          : `solo::${item.id}`;
      const list = map.get(key) || [];
      list.push(item);
      map.set(key, list);
    }
    return Array.from(map.entries()).map(([key, list]) => {
      const latest = list[0];
      return {
        key,
        entityType: latest.entityType,
        entityId: latest.entityId,
        title: latest.title,
        summary: list.length === 1 ? latest.message || latest.title : `${list.length} workflow events`,
        latestAt: latest.at,
        actionRoute: latest.actionRoute,
        eventCount: list.length,
        items: list,
      } as WorkboardActivityGroup;
    });
  }, [activityQuery.data]);

  const selectKpi = (key: KpiKey) => {
    setKpiFilter(key);
    setDueWindow('all');
    if (key === 'action_required') setActiveTab('needs_action');
    else if (key === 'responsibilities') setActiveTab('responsibilities');
    else if (key === 'waiting_on_others') setActiveTab('waiting_on_others');
    else if (key === 'exceptions') setActiveTab('overdue_at_risk');
  };

  const baseRows = useMemo((): EnterpriseWorkItemProjection[] => {
    if (!data) return [];
    let rows: EnterpriseWorkItemProjection[] = [];
    switch (activeTab) {
      case 'needs_action':
        rows = data.needs_action;
        break;
      case 'responsibilities':
        rows = data.responsibilities ?? [];
        break;
      case 'initiated':
        rows = data.initiated;
        break;
      case 'waiting_on_others':
        rows = data.waiting_on_others;
        break;
      case 'overdue_at_risk':
        rows = data.overdue_at_risk;
        break;
      default:
        rows = [];
    }
    return rows.filter((r) => r.visibility !== 'secondary');
  }, [data, activeTab]);

  const overdueInAction = useMemo(
    () => (data?.needs_action ?? []).filter((r) => matchesDueWindow(r, 'overdue')).length,
    [data]
  );

  const queueRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortRows(
      baseRows.filter((r) => matchesDueWindow(r, dueWindow)).filter((r) => matchesSearch(r, q)),
      sortMode
    );
  }, [baseRows, dueWindow, search, sortMode]);

  const queueGroups = useMemo(() => buildGroups(queueRows, groupByType), [queueRows, groupByType]);

  const openDocument = (row: EnterpriseWorkItemProjection) => {
    navigate(row.actionRoute || '/home/alerts');
  };

  const onCta = (row: EnterpriseWorkItemProjection) => {
    if (row.source === 'approval_inbox' && row.ctaLabel === 'Approve') {
      setSelected(row);
      return;
    }
    openDocument(row);
  };

  if (query.isLoading && !data) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const draftCount = draftsQuery.data?.items?.length ?? 0;

  const kpiCards: Array<{
    key: KpiKey;
    title: string;
    value: number;
    icon: React.ReactNode;
    warn?: boolean;
  }> = [
    {
      key: 'action_required',
      title: t('myActions'),
      value: kpis.action_required,
      icon: <CheckCircleOutlined />,
      warn: kpis.action_required > 0,
    },
    {
      key: 'responsibilities',
      title: t('myResponsibilities'),
      value: kpis.responsibilities ?? 0,
      icon: <FlagOutlined />,
    },
    {
      key: 'exceptions',
      title: t('exceptions'),
      value: kpis.exceptions,
      icon: <WarningOutlined />,
      warn: kpis.exceptions > 0,
    },
    {
      key: 'waiting_on_others',
      title: t('waitingOnOthers'),
      value: kpis.waiting_on_others,
      icon: <TeamOutlined />,
    },
  ];

  const renderRows = (rows: EnterpriseWorkItemProjection[], limit?: number) => {
    const slice = limit != null ? rows.slice(0, limit) : rows;
    return slice.map((row) => (
      <QueueRow
        key={row.id}
        row={row}
        density={density}
        onOpen={() => setSelected(row)}
        onCta={() => onCta(row)}
        remind={row.sourceRefId ? () => remindMutation.mutate(row.sourceRefId!) : undefined}
        remindLoading={remindMutation.isPending}
      />
    ));
  };

  return (
    <div style={{ minWidth: 0 }}>
      <Row justify="space-between" align="top" style={{ marginBottom: 16 }} gutter={[12, 12]}>
        <Col flex="auto" style={{ minWidth: 0 }}>
          <Title level={3} style={{ margin: 0, color: ORDAN_TEAL }}>
            {t('title')}
          </Title>
          <Text type="secondary">{t('subtitle')}</Text>
          <Paragraph style={{ margin: '8px 0 0', maxWidth: 720 }}>
            {dayjs().hour() < 12
              ? t('greetingMorning')
              : dayjs().hour() < 17
                ? t('greetingAfternoon')
                : t('greetingEvening')}
            , {displayName} · {attentionSentence(kpis)}
          </Paragraph>
        </Col>
        <Col>
          <Space wrap>
            <Link to="/home/drafts">
              {t('draftsLink')}
              {draftCount > 0 ? ` (${draftCount})` : ''}
            </Link>
            <Link to="/home/company-overview">{t('companyOverviewLink')}</Link>
            <Button
              icon={<ReloadOutlined />}
              loading={query.isFetching}
              onClick={() => {
                void query.refetch();
                void activityQuery.refetch();
                void draftsQuery.refetch();
              }}
            >
              {t('refresh')}
            </Button>
          </Space>
        </Col>
      </Row>

      {query.error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Could not load workboard"
          description={(query.error as Error)?.message || 'Retry shortly.'}
          action={
            <Button size="small" onClick={() => query.refetch()}>
              Retry
            </Button>
          }
        />
      )}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {kpiCards.map((card) => {
          const active = kpiFilter === card.key;
          return (
            <Col xs={12} md={6} key={card.key}>
              <Tooltip title={KPI_HELP[card.key]}>
                <Card
                  size="small"
                  hoverable
                  onClick={() => selectKpi(card.key)}
                  style={{
                    borderColor: active ? ORDAN_TEAL : undefined,
                    borderWidth: active ? 2 : 1,
                    background: active ? '#F7F8F7' : '#fff',
                  }}
                >
                  <Space align="start">
                    <span style={{ color: card.warn ? '#cf1322' : ORDAN_TEAL, fontSize: 18 }}>
                      {card.icon}
                    </span>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                        {card.title}
                      </Text>
                      <Text
                        strong
                        style={{
                          fontSize: 28,
                          lineHeight: 1.1,
                          color: card.warn && card.value > 0 ? '#cf1322' : ORDAN_TEAL,
                        }}
                      >
                        {card.value}
                      </Text>
                    </div>
                  </Space>
                </Card>
              </Tooltip>
            </Col>
          );
        })}
      </Row>

      <Row gutter={[16, 16]} wrap>
        <Col xs={24} lg={16} xl={17} style={{ minWidth: 0 }}>
          <Card
            title={
              <Space>
                <UnorderedListOutlined />
                My Action Queue
              </Space>
            }
            extra={
              <Space wrap size={8}>
                <Segmented
                  size="small"
                  value={density}
                  onChange={(v) => {
                    const next = v as DensityMode;
                    setDensity(next);
                    writePref(DENSITY_KEY, next);
                  }}
                  options={[
                    { label: 'Compact', value: 'compact' },
                    { label: 'Comfortable', value: 'comfortable' },
                  ]}
                />
              </Space>
            }
          >
            <Space wrap style={{ marginBottom: 12, width: '100%' }} size={[8, 8]}>
              <Segmented
                size="small"
                value={dueWindow}
                onChange={(v) => {
                  setDueWindow(v as DueWindow);
                  setKpiFilter(null);
                }}
                options={[
                  { label: `In this view · ${baseRows.length}`, value: 'all' },
                  {
                    label: `Overdue in view · ${baseRows.filter((r) => matchesDueWindow(r, 'overdue')).length}`,
                    value: 'overdue',
                  },
                  { label: 'High priority', value: 'critical' },
                  { label: 'Today', value: 'today' },
                ]}
              />
              <Input.Search
                allowClear
                placeholder="Search queue"
                style={{ width: 200 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Select
                size="small"
                value={sortMode}
                style={{ width: 140 }}
                onChange={(v) => setSortMode(v)}
                options={[
                  { value: 'impact', label: 'Sort: impact' },
                  { value: 'waiting', label: 'Sort: waiting' },
                  { value: 'due', label: 'Sort: due' },
                  { value: 'document', label: 'Sort: document' },
                ]}
              />
              <Space size={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Group by type
                </Text>
                <Switch
                  size="small"
                  checked={groupByType}
                  onChange={(checked) => {
                    setGroupByType(checked);
                    writePref(GROUP_KEY, checked ? '1' : '0');
                  }}
                />
              </Space>
            </Space>

            <Tabs
              activeKey={activeTab}
              onChange={(k) => {
                setKpiFilter(null);
                setActiveTab(k as TabKey);
              }}
              items={[
                {
                  key: 'needs_action',
                  label: (
                    <Tooltip title={TAB_HELP.needs_action}>
                      <span>Action ({data?.needs_action.length ?? 0})</span>
                    </Tooltip>
                  ),
                },
                {
                  key: 'responsibilities',
                  label: (
                    <Tooltip title={TAB_HELP.responsibilities}>
                      <span>Responsibilities ({data?.responsibilities?.length ?? 0})</span>
                    </Tooltip>
                  ),
                },
                {
                  key: 'initiated',
                  label: (
                    <Tooltip title={TAB_HELP.initiated}>
                      <span>Initiated ({data?.initiated.length ?? 0})</span>
                    </Tooltip>
                  ),
                },
                {
                  key: 'waiting_on_others',
                  label: (
                    <Tooltip title={TAB_HELP.waiting_on_others}>
                      <span>Waiting ({data?.waiting_on_others.length ?? 0})</span>
                    </Tooltip>
                  ),
                },
                {
                  key: 'overdue_at_risk',
                  label: (
                    <Tooltip title={TAB_HELP.overdue_at_risk}>
                      <span>Exceptions ({data?.overdue_at_risk.length ?? 0})</span>
                    </Tooltip>
                  ),
                },
              ]}
            />

            {activeTab === 'overdue_at_risk' && (data?.exception_breakdown?.length ?? 0) > 0 && (
              <Space wrap size={[8, 8]} style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Sources
                </Text>
                {(data?.exception_breakdown ?? []).map((b) => (
                  <Tag key={b.source} color="red">
                    {b.label}: {b.count}
                  </Tag>
                ))}
              </Space>
            )}

            {query.isFetching && !queueRows.length ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
              </div>
            ) : queueRows.length === 0 ? (
              <Empty description="Nothing in this queue" />
            ) : groupByType ? (
              queueGroups.map((g) => {
                const expanded = expandedGroups[g.key] === true;
                const previewLimit = expanded || g.rows.length <= 2 ? undefined : 2;
                return (
                  <div
                    key={g.key}
                    style={{
                      border: '1px solid #E8ECEB',
                      borderRadius: 8,
                      padding: '10px 12px',
                      marginBottom: 12,
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 4,
                      }}
                    >
                      <Text strong>{g.label}</Text>
                      <Tag style={{ margin: 0 }}>{g.rows.length}</Tag>
                    </div>
                    {renderRows(g.rows, previewLimit)}
                    {g.rows.length > 2 && (
                      <Button
                        type="link"
                        size="small"
                        style={{ paddingLeft: 0 }}
                        onClick={() =>
                          setExpandedGroups((prev) => ({ ...prev, [g.key]: !expanded }))
                        }
                      >
                        {expanded
                          ? 'Show fewer'
                          : `View all ${g.rows.length} in this group`}
                      </Button>
                    )}
                  </div>
                );
              })
            ) : (
              <div style={{ minWidth: 0 }}>{renderRows(queueRows)}</div>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8} xl={7} style={{ minWidth: 0 }}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card
              title={
                <Space>
                  <FlagOutlined />
                  My Responsibility Overview
                </Space>
              }
            >
              {(data?.responsibility_overview ?? []).length === 0 ? (
                <Text type="secondary">No role-derived responsibilities in scope.</Text>
              ) : (
                (data?.responsibility_overview ?? []).map((row, idx, arr) => (
                  <div
                    key={row.key}
                    style={{
                      padding: '10px 0',
                      borderBottom: idx < arr.length - 1 ? '1px solid #E8ECEB' : undefined,
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <Text strong style={{ display: 'block' }}>
                        {row.title}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {row.subtitle}
                      </Text>
                    </div>
                    <Tag
                      color={
                        row.badge === 'Clear' || row.badge === 'Review'
                          ? 'default'
                          : row.key === 'workflow_exceptions'
                            ? 'default'
                            : 'orange'
                      }
                      style={{ margin: 0, cursor: 'pointer' }}
                      onClick={() => {
                        if (row.key === 'purchase_requisitions') selectKpi('action_required');
                        else if (row.key === 'workflow_exceptions') selectKpi('exceptions');
                        else if (String(row.badge).includes('overdue')) selectKpi('exceptions');
                        else selectKpi('responsibilities');
                      }}
                    >
                      {row.badge}
                    </Tag>
                  </div>
                ))
              )}
            </Card>
            <Card
              title={
                <Space>
                  <BellOutlined />
                  Attention
                </Space>
              }
              extra={<Link to="/home/alerts">All alerts</Link>}
              loading={activityQuery.isLoading && !activityQuery.data}
              styles={{ body: { minWidth: 0, overflow: 'hidden' } }}
            >
              <AttentionPanel
                groups={activityGroups}
                overdueCount={overdueInAction}
                personalFollowups={data?.personal_followups ?? []}
                onNavigate={(r) => r && navigate(r)}
              />
            </Card>
          </Space>
        </Col>
      </Row>

      <Drawer
        title={selected?.businessDescription || selected?.title || 'Work item'}
        open={!!selected}
        onClose={() => setSelected(null)}
        width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 24 : 560)}
        destroyOnClose
        extra={
          selected ? (
            <Button
              type="primary"
              style={{ background: ORDAN_TEAL, borderColor: ORDAN_TEAL }}
              onClick={() => openDocument(selected)}
            >
              Open document
            </Button>
          ) : null
        }
      >
        {selected && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Text type="secondary" style={{ display: 'block' }}>
                Required action
              </Text>
              <Text strong>{selected.actionRequired || '—'}</Text>
            </div>
            {selected.contextSummary && (
              <div>
                <Text type="secondary" style={{ display: 'block' }}>
                  Context
                </Text>
                <Text>{selected.contextSummary}</Text>
              </div>
            )}
            {selected.delayConsequence && (
              <Alert type="info" showIcon message={selected.delayConsequence} />
            )}
            {selected.stale && (
              <Alert
                type="warning"
                showIcon
                message="Stale"
                description={
                  selected.staleReason ||
                  'No meaningful workflow activity within the inactivity threshold. Distinct from overdue and exceptions.'
                }
              />
            )}
            <Row gutter={12}>
              <Col span={12}>
                <Text type="secondary" style={{ display: 'block' }}>
                  Module
                </Text>
                <Text>{selected.module}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ display: 'block' }}>
                  Status
                </Text>
                <Text>{selected.workflowStepLabel || selected.status}</Text>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}>
                <Text type="secondary" style={{ display: 'block' }}>
                  Waiting
                </Text>
                <Text>
                  {selected.waitingSinceDays != null ? `${selected.waitingSinceDays} days` : '—'}
                </Text>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ display: 'block' }}>
                  Ownership
                </Text>
                <Text>{ownershipLabel(selected.ownershipRole) || '—'}</Text>
              </Col>
            </Row>
            {selected.partyName && (
              <div>
                <Text type="secondary" style={{ display: 'block' }}>
                  Party
                </Text>
                <Text>{selected.partyName}</Text>
              </div>
            )}
            {selected.amountDisplay && (
              <div>
                <Text type="secondary" style={{ display: 'block' }}>
                  Amount
                </Text>
                <Text>{selected.amountDisplay}</Text>
              </div>
            )}
            {selected.currentResponsibleParty && (
              <div>
                <Text type="secondary" style={{ display: 'block' }}>
                  Current responsible
                </Text>
                <Text>{selected.currentResponsibleParty}</Text>
              </div>
            )}
            {selected.blockingReason && (
              <Alert type="warning" showIcon message={selected.blockingReason} />
            )}
            {selected.source === 'responsibility_intelligence' && (
              <Space wrap>
                <Button
                  type="primary"
                  style={{ background: ORDAN_TEAL, borderColor: ORDAN_TEAL }}
                  onClick={() => openDocument(selected)}
                >
                  View purchase order
                </Button>
                {selected.sourceRefId && (
                  <Button
                    loading={acknowledgeRieMutation.isPending}
                    onClick={() => acknowledgeRieMutation.mutate()}
                  >
                    Acknowledge monitor
                  </Button>
                )}
              </Space>
            )}
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                <FlagOutlined /> Personal follow-up
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                Your reminder only — does not change approval or Unified Task due dates.
              </Text>
              <Space wrap>
                <DatePicker
                  value={followUpDate ? dayjs(followUpDate) : null}
                  onChange={(d) => setFollowUpDate(d ? d.format('YYYY-MM-DD') : null)}
                  allowClear={false}
                />
                <Input
                  placeholder="Optional note"
                  style={{ width: 200 }}
                  value={followUpNote}
                  onChange={(e) => setFollowUpNote(e.target.value)}
                  maxLength={200}
                />
                <Button
                  type="primary"
                  style={{ background: ORDAN_TEAL, borderColor: ORDAN_TEAL }}
                  loading={followUpMutation.isPending}
                  onClick={() => followUpMutation.mutate()}
                >
                  Save follow-up
                </Button>
                {selected.personalFollowUpDate && (
                  <Button
                    loading={clearFollowUpMutation.isPending}
                    onClick={() => clearFollowUpMutation.mutate()}
                  >
                    Clear
                  </Button>
                )}
              </Space>
            </div>
            {(selected.bucket === 'waiting_on_others' || selected.bucket === 'initiated') &&
              selected.source === 'approval_inbox' &&
              selected.sourceRefId &&
              selected.status === 'pending_approval' && (
                <Button
                  loading={remindMutation.isPending}
                  onClick={() => remindMutation.mutate(selected.sourceRefId!)}
                >
                  Remind approvers
                </Button>
              )}
            {selected.source === 'approval_inbox' && (
              <div>
                <Title level={5} style={{ marginTop: 8 }}>
                  Approval
                </Title>
                <ApprovalPanel
                  entityType={selected.entityType}
                  entityId={selected.entityId}
                  showAiBrief={false}
                  extraInvalidateQueryKeys={[['home-workboard', userId, companyId]]}
                />
              </div>
            )}
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default MyWorkboardPage;
